import React, { useCallback } from 'react';
import { useAuth } from 'context/authContext';
import { backofficeOrganisationApi } from 'api/backoffice/organisation';
import type { OrgFeatures, FeatureValue } from 'api/interface/organisation';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface FeatureContextValue {
    features: OrgFeatures;
    loading: boolean;
    isEnabled: (key: string) => boolean;
    getValue: (key: string) => string | null;
    getLabel: (key: string, defaultLabel: string) => string;
    reload: () => void;
}

const FeatureContext = React.createContext<FeatureContextValue>({
    features: {},
    loading: false,
    isEnabled: () => true,
    getValue: () => null,
    getLabel: (_key, defaultLabel) => defaultLabel,
    reload: () => {},
});

export const useFeatureContext = () => React.useContext(FeatureContext);

function resolve(features: OrgFeatures, key: string): FeatureValue | undefined {
    if (features.modules && key in features.modules) {
        return features.modules[key];
    }
    const val = features[key];
    if (val !== undefined && typeof val !== 'object') return val;
    if (Array.isArray(val)) return val;
    return undefined;
}

export const useFeature = (key: string): boolean => {
    const { isEnabled } = useFeatureContext();
    return isEnabled(key);
};

export const useFeatureValue = (key: string): string | null => {
    const { getValue } = useFeatureContext();
    return getValue(key);
};

export const useLabel = (key: string, defaultLabel: string): string => {
    const { getLabel } = useFeatureContext();
    return getLabel(key, defaultLabel);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FeatureProvider = (props: any): JSX.Element => {
    const { organisationID, isAuthenticated } = useAuth();
    const queryClient = useQueryClient();

    const { data: features = {}, isLoading } = useQuery({
        queryKey: ['orgFeatures', organisationID],
        queryFn: async () => {
            const res =
                await backofficeOrganisationApi.getOrgFeatures(organisationID);
            if (!res.error && res.payload) return res.payload;
            return {} as OrgFeatures;
        },
        enabled: !!organisationID && isAuthenticated,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const reload = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: ['orgFeatures', organisationID],
        });
    }, [queryClient, organisationID]);

    const isEnabled = useCallback(
        (key: string): boolean => {
            // Don't show anything while features are still loading
            if (isLoading) return false;
            // Walk up the hierarchy: "a.b.c" checks "a", then "a.b", then "a.b.c"
            const parts = key.split('.');
            for (let i = 1; i <= parts.length; i++) {
                const segment = parts.slice(0, i).join('.');
                const val = resolve(features, segment);
                if (val === false) return false;
            }
            return true;
        },
        [features, isLoading]
    );

    const getValue = useCallback(
        (key: string): string | null => {
            const val = resolve(features, key);
            if (val === undefined) return null;
            if (Array.isArray(val)) return val.join(',');
            return String(val);
        },
        [features]
    );

    const getLabel = useCallback(
        (key: string, defaultLabel: string): string => {
            const val = resolve(features, key);
            if (typeof val === 'string' && val.length > 0) return val;
            return defaultLabel;
        },
        [features]
    );

    return (
        <FeatureContext.Provider
            value={{
                features,
                loading: isLoading,
                isEnabled,
                getValue,
                getLabel,
                reload,
            }}
            {...props}
        />
    );
};
